import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  Divider,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import useApiEp from "../hooks/useApiEp";

const CreditUsageHistory = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPartner, setSelectedPartner] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: "",
    endDate: "",
  });

  // Build query params
  const queryParams = new URLSearchParams();
  if (appliedFilters.startDate)
    queryParams.append("startDate", appliedFilters.startDate);
  if (appliedFilters.endDate)
    queryParams.append("endDate", appliedFilters.endDate);
  const queryString = queryParams.toString();

  const {
    data: customersUsage,
    isLoading,
    isError,
    error,
  } = useApiEp(
    `/credit/get-credit-usage-all-users${queryString ? `?${queryString}` : ""}`
  );

  // INR formatter
  const inrFormat = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);

  // Handle filter apply
  const handleApplyFilter = () => {
    setAppliedFilters({ startDate, endDate });
  };

  // Handle reset
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setSelectedPartner("");
    setAppliedFilters({ startDate: "", endDate: "" });
  };

  // Build final rows
  const processed = customersUsage?.history?.map((item) => {
    const phoneNumberId = item.phoneNumberId;

    // Find matching customer from accounts list
    const match = customersUsage.accounts.find(
      (acc) => String(acc.phoneNumberId) === String(phoneNumberId)
    );

    return {
      customerId: match?.customerId || "(Unknown)",
      phoneNumberId: phoneNumberId || "(No Phone)",
      wabaAccountId: match?.wabaAccountId || "-",
      partner: match?.partner || "-",
      totalUsage: item.totalUsage,
    };
  });

  // Get unique partners for dropdown
  const partnerOptions = useMemo(() => {
    if (!customersUsage?.accounts) return [];

    const partners = customersUsage.accounts
      .map((acc) => acc.partner)
      .filter((partner) => partner); // Remove null/undefined/empty

    return [...new Set(partners)].sort();
  }, [customersUsage]);

  // Filter by selected partner
  const filteredData = useMemo(() => {
    if (!selectedPartner || selectedPartner === "all") {
      return processed;
    }
    return processed?.filter((row) => row.partner === selectedPartner);
  }, [processed, selectedPartner]);

  // Sort by highest usage
  const sortedData = filteredData?.sort((a, b) => b.totalUsage - a.totalUsage);

  // Calculate Total Usage (filtered)
  const totalUsageSum = filteredData?.reduce(
    (sum, item) => sum + item.totalUsage,
    0
  );

  return (
    <Container sx={{ mt: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Credit Usage – All Users
      </Typography>

      {/* Date Range Filter */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Filter by Date Range
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box display="flex" gap={1}>
              <Button variant="contained" onClick={handleApplyFilter} fullWidth>
                Apply
              </Button>
              <Button variant="outlined" onClick={handleReset} fullWidth>
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Partner Filter */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Filter by Partner
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Partner</InputLabel>
              <Select
                value={selectedPartner}
                label="Partner"
                onChange={(e) => setSelectedPartner(e.target.value)}
              >
                <MenuItem value="all">All Partners</MenuItem>
                {partnerOptions.map((partner) => (
                  <MenuItem key={partner} value={partner}>
                    {partner}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary - Total Usage */}
      {!isLoading && !isError && (
        <Paper
          elevation={3}
          sx={{
            mb: 3,
            p: 2,
            background: "#f9fafb",
            borderLeft: "6px solid #1976d2",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Total Usage
            {selectedPartner && selectedPartner !== "all" && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                ml={1}
              >
                (Partner: {selectedPartner})
              </Typography>
            )}
            {(appliedFilters.startDate || appliedFilters.endDate) && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                ml={1}
              >
                (
                {appliedFilters.startDate && `from ${appliedFilters.startDate}`}
                {appliedFilters.startDate && appliedFilters.endDate && " "}
                {appliedFilters.endDate && `to ${appliedFilters.endDate}`})
              </Typography>
            )}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="h5" color="success.main" fontWeight={700}>
            {inrFormat(totalUsageSum || 0)}
          </Typography>
        </Paper>
      )}

      {/* Loading */}
      {isLoading && (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {isError && (
        <Typography color="error" mt={3}>
          {error?.message || "Something went wrong"}
        </Typography>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead sx={{ background: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Customer Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Phone Number ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>WABA ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Partner</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  Total Usage (INR)
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedData?.map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.customerId}</TableCell>
                  <TableCell>{row.phoneNumberId}</TableCell>
                  <TableCell>{row.wabaAccountId}</TableCell>
                  <TableCell>{row.partner}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {inrFormat(row.totalUsage)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default CreditUsageHistory;
